import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { X, Mail, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function EmailPreviewModal({ reflectionId, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [error, setError] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');

  useEffect(() => {
    loadEmailPreview();
  }, [reflectionId]);

  const loadEmailPreview = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/weekly-reflections/${reflectionId}/preview-email`);
      setEmailData(response.data);
      setRecipientEmail(response.data.clientEmail); // Auto-fill client email
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        setError('Session expired. Please log out and log back in.');
      } else {
        setError('Failed to load email preview: ' + (err.response?.data?.error || err.message));
      }
      console.error('Error loading email preview:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast('Please enter a valid email address');
      return;
    }

    try {
      setSending(true);

      const response = await axios.post(`${API_URL}/weekly-reflections/${reflectionId}/send`, {
        recipientEmail: recipientEmail
      });

      toast.success('Email sent successfully!');

      if (onSent) {
        onSent();
      }
    } catch (err) {
      console.error('Error sending email:', err);
      console.error('Error response status:', err.response?.status);
      console.error('Error response data:', err.response?.data);

      let errorMsg = 'Unknown error';

      if (err.response?.status === 401 || err.response?.status === 403) {
        errorMsg = 'Session expired. Please log out and log back in.';
      } else if (err.response?.status === 400) {
        errorMsg = err.response?.data?.error || 'This reflection has already been sent.';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
        // Include details if available
        if (err.response?.data?.details) {
          const details = err.response.data.details;
          if (typeof details === 'object') {
            errorMsg += '\n\nDetails: ' + JSON.stringify(details, null, 2);
          } else {
            errorMsg += '\n\nDetails: ' + details;
          }
        }
      } else if (err.message) {
        errorMsg = err.message;
      }

      console.error('Full error response:', err.response?.data);
      toast.error('Failed to send email: ' + errorMsg);
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-3 flex-1">
            <Mail className="w-6 h-6 text-[var(--accent)]" />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-2">Email Preview</h2>
              {emailData && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-400">To:</label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    className="flex-1 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[var(--accent)] max-w-md"
                    placeholder="recipient@email.com"
                  />
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors ml-4"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--accent)]/30 border-t-[var(--accent)] mx-auto mb-4"></div>
                <p className="text-gray-400">Loading email preview...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
              {error}
            </div>
          )}

          {emailData && !loading && (
            <div className="space-y-4">
              {/* Email Subject */}
              <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-1">Subject:</p>
                <p className="text-white font-medium">{emailData.subject}</p>
              </div>

              {/* Email Body Preview */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div
                  className="email-preview"
                  dangerouslySetInnerHTML={{ __html: emailData.htmlContent }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-[var(--border-color)]">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[var(--bg-primary)] text-white rounded-lg hover:bg-[var(--bg-secondary)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || loading || error}
            className="px-6 py-3 bg-[var(--accent)] text-white rounded-lg hover:bg-[var(--accent)] transition-colors flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Send to Client</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Global styles for email preview */}
      <style>{`
        .email-preview {
          font-family: Arial, sans-serif;
        }
        .email-preview a {
          pointer-events: none;
          cursor: default;
        }
      `}</style>
    </div>
  );
}

export default EmailPreviewModal;
